import { Theme } from "@mui/material";

export const videoPlayerStyles = (theme: Theme, hasTitle?: boolean) => {
  const isDark = theme.palette.mode === "dark";

  const controlsBg = isDark
    ? theme.palette.audioPlayer?.bg || "rgba(0, 0, 0, 0.8)"
    : "rgba(0, 0, 0, 0.75)";

  const controlsTextColor = isDark ? "#fff" : "#fff";
  const controlsIconColor = isDark ? "#fff" : "#fff";

  const progressBarBg = isDark
    ? "rgba(255, 255, 255, 0.3)"
    : "rgba(255, 255, 255, 0.4)";

  const bufferedBarBg = isDark
    ? "rgba(255, 255, 255, 0.5)"
    : "rgba(255, 255, 255, 0.6)";

  const selectBorderColor = isDark
    ? "rgba(255, 255, 255, 0.3)"
    : "rgba(255, 255, 255, 0.4)";
  const selectBorderHoverColor = isDark
    ? "rgba(255, 255, 255, 0.5)"
    : "rgba(255, 255, 255, 0.6)";
  const selectBorderFocusColor = isDark ? "#fff" : "#fff";

  return {
    container: {
      display: "flex",
      flexDirection: "column",
      borderRadius: "18px 18px 0px 0px",
      overflow: "visible",
      backgroundColor: hasTitle
        ? theme.palette.audioPlayer?.bg || (isDark ? "#000" : "#fff")
        : "transparent",
      maxWidth: "100%",
      width: "100%",
      "&:fullscreen": {
        borderRadius: 0,
        width: "100vw",
        height: "100vh",
        maxWidth: "100vw",
        backgroundColor: "#000",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      },
      "&:-webkit-full-screen": {
        borderRadius: 0,
        width: "100vw",
        height: "100vh",
        maxWidth: "100vw",
        backgroundColor: "#000",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      },
      "&:-moz-full-screen": {
        borderRadius: 0,
        width: "100vw",
        height: "100vh",
        maxWidth: "100vw",
        backgroundColor: "#000",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      },
      "&:-ms-fullscreen": {
        borderRadius: 0,
        width: "100vw",
        height: "100vh",
        maxWidth: "100vw",
        backgroundColor: "#000",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      },
    },
    videoWrapper: {
      position: "relative",
      width: "100%",
      backgroundColor: "transparent",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
      minHeight: "200px",
      ":fullscreen &": {
        width: "100vw",
        height: "100vh",
        minHeight: "100vh",
      },
    },
    video: {
      width: "100%",
      height: "auto",
      maxHeight: "600px",
      display: "block",
      objectFit: "contain",
      margin: 0,
      padding: 0,
      flexShrink: 0,
      ":fullscreen &": {
        width: "100vw",
        height: "100vh",
        maxHeight: "100vh",
        objectFit: "contain",
      },
    },
    controlsContainer: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      backgroundColor: controlsBg,
      padding: "8px 12px",
      gap: "4px",
      width: "100%",
    },
    controlsRow: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      width: "100%",
    },
    playButton: {
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: "32px",
      minHeight: "32px",
      color: controlsIconColor,
      flexShrink: 0,
      "&:hover": {
        opacity: 0.8,
      },
    },
    progressContainer: {
      flex: 1,
      display: "flex",
      alignItems: "center",
      gap: "8px",
      minWidth: 0,
      margin: "0 4px",
      flexShrink: 1,
    },
    progressBar: {
      flex: 1,
      height: "4px",
      backgroundColor: progressBarBg,
      borderRadius: "2px",
      cursor: "pointer",
      position: "relative",
      overflow: "hidden",
    },
    bufferedBar: {
      position: "absolute",
      top: 0,
      left: 0,
      height: "100%",
      backgroundColor: bufferedBarBg,
      borderRadius: "2px",
      zIndex: 1,
      transition: "width 0.1s linear",
    },
    progressFill: {
      position: "absolute",
      top: 0,
      left: 0,
      height: "100%",
      backgroundColor: "#06B6D4",
      borderRadius: "2px",
      zIndex: 2,
      transition: "width 0.1s linear",
    },
    timeDisplay: {
      display: "flex",
      alignItems: "center",
      gap: "4px",
      minWidth: "fit-content",
      fontSize: "12px",
      color: controlsTextColor,
      whiteSpace: "nowrap",
      marginRight: "4px",
      flexShrink: 0,
    },
    volumeContainer: {
      display: "flex",
      alignItems: "center",
      gap: "4px",
      minWidth: "fit-content",
      flexShrink: 0,
    },
    volumeButton: {
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: "24px",
      minHeight: "24px",
      color: controlsIconColor,
      "&:hover": {
        opacity: 0.8,
      },
    },
    volumeSlider: {
      width: "60px",
      height: "4px",
      cursor: "pointer",
    },
    fullscreenButton: {
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: "24px",
      minHeight: "24px",
      color: controlsIconColor,
      "&:hover": {
        opacity: 0.8,
      },
    },
    loadingContainer: {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    errorContainer: {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      color: "#fff",
      textAlign: "center",
      padding: "16px",
    },
    title: {
      fontWeight: 600,
      color: "#06B6D4",
      whiteSpace: "normal",
      wordBreak: "break-word",
      marginBottom: "8px",
    },
    speedSelect: {
      width: "50px",
      fontSize: "0.7rem",
      borderRadius: "4px",
      backgroundColor: "transparent",
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: selectBorderColor,
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: selectBorderHoverColor,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: selectBorderFocusColor,
      },
      "& .MuiSvgIcon-root": {
        color: controlsIconColor,
      },
      color: controlsIconColor,
      "& .MuiSelect-select": {
        padding: "4px 8px",
        fontSize: "12px",
      },
    },
    centerPlayButton: {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: { xs: "60px", sm: "80px" },
      height: { xs: "60px", sm: "80px" },
      borderRadius: "50%",
      backgroundColor: "rgba(0, 0, 0, 0.6)",
      border: { xs: "2px solid #06B6D4", sm: "3px solid #06B6D4" },
      transition: "all 0.3s ease",
      zIndex: 10,
      "&:hover": {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        transform: "translate(-50%, -50%) scale(1.1)",
      },
      "&:active": {
        transform: "translate(-50%, -50%) scale(0.95)",
      },
    },
  };
};
